import 'react-datepicker/dist/react-datepicker.css';
import React, { useState, useEffect, useCallback } from 'react';
import './styles/App.css';
import './styles/responsive.css';
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    BarElement,
    Title,
    Tooltip,
    Legend,
    Filler
} from 'chart.js';

import { ThemeProvider } from './context/ThemeContext';
import Notification from './components/common/Notification';
import Sidebar from './components/common/Sidebar';
import AuthPage from './components/auth/AuthPage';
import DashboardView from './components/views/DashboardView';
import PointOfSaleView from './components/views/PointOfSaleView';
import SalesView from './components/views/SalesView';
import InventoryView from './components/views/InventoryView';
import BillSettingsView from './components/views/BillSettingsView';
import LeadsView from './components/views/LeadsView';
import CustomersView from './components/views/CustomersView';
import TasksView from './components/views/TasksView';
import UsersView from './components/views/UsersView';
import SuperAdminView from './components/views/SuperAdminView';
import BillingView from './components/views/BillingView';
import PurchaseBillView from './components/views/PurchaseBillView';
import PurchaseHistoryView from './components/views/PurchaseHistoryView';
import ProfitReportView from './components/views/ProfitReportView';
import ClosingMarginView from './components/views/ClosingMarginView';
import PaymentLedgerView from './components/views/PaymentLedgerView';
import ExpensesView from './components/views/ExpensesView';
import { fetchAuth } from './utils/api';


ChartJS.register(
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    BarElement,
    Title,
    Tooltip,
    Legend,
    Filler
);

function App() {
    const [user, setUser] = useState(null);
    const [view, setView] = useState('dashboard');
    const [isSidebarOpen, setIsSidebarOpen] = useState(() => window.innerWidth > 768);
    const [notification, setNotification] = useState({ show: false, message: '', type: '' });
    const [companySettings, setCompanySettings] = useState(null);
    const [companyDetails, setCompanyDetails] = useState(null);

    const fetchCompanySettings = useCallback(async () => {
        if (user && user.role !== 'SuperAdmin') {
            try {
                const [settingsRes, detailsRes] = await Promise.all([
                    fetchAuth('/api/bill-settings'),
                    fetchAuth('/api/company/details')
                ]);

                let settingsData = null;
                let detailsData = null;

                if (settingsRes.ok) settingsData = await settingsRes.json();
                if (detailsRes.ok) detailsData = await detailsRes.json();

                setCompanyDetails(detailsData);

                // Merge details (like addons, subscriptionPlan) into companySettings so Sidebar can read them
                if (settingsData || detailsData) {
                    setCompanySettings({
                        ...(settingsData || {}),
                        addons: detailsData?.addons || {},
                        subscriptionPlan: detailsData?.subscriptionPlan || 'Free',
                        companyName: detailsData?.companyName || settingsData?.companyName || 'Stockify User'
                    });
                }
            } catch (err) {
                console.error("Could not fetch company settings:", err);
            }
        }
    }, [user]);

    useEffect(() => {
        const token = sessionStorage.getItem('token');
        const role = sessionStorage.getItem('role');
        const username = sessionStorage.getItem('username');
        if (token && role && username) {
            setUser({ token, role, username });

            const urlParams = new URLSearchParams(window.location.search);
            const viewFromUrl = urlParams.get('view');

            if (viewFromUrl) {
                setView(viewFromUrl);
                window.history.replaceState({}, document.title, window.location.pathname);
            } else {
                if (role === 'Member') setView('new_sale');
                else if (role === 'SuperAdmin') setView('superadmin_dashboard');
                else setView('dashboard');
            }
        }
    }, []);

    useEffect(() => {
        if (user) {
            fetchCompanySettings();
        }
    }, [user, fetchCompanySettings]);

    const showNotification = useCallback((message, type = 'success', duration = 4000) => {
        setNotification({ show: true, message, type });
        setTimeout(() => setNotification(n => ({ ...n, show: false })), duration);
    }, []);

    const handleLogout = useCallback(() => {
        sessionStorage.clear();
        setUser(null);
        setCompanySettings(null);
        setView('dashboard');
    }, []);

    const handleLoginSuccess = (loginData) => {
        sessionStorage.setItem('token', loginData.token);
        sessionStorage.setItem('role', loginData.role);
        sessionStorage.setItem('username', loginData.username);
        setUser(loginData);

        const redirectPath = localStorage.getItem('redirectAfterLogin');
        if (redirectPath) {
            setView(redirectPath);
            localStorage.removeItem('redirectAfterLogin');
        } else {
            if (loginData.role === 'Member') {
                setView('new_sale');
            } else if (loginData.role === 'SuperAdmin') {
                setView('superadmin_dashboard');
            } else {
                setView('dashboard');
            }
        }
    };

    const handleApiError = useCallback(async (error) => {
        let errorData;
        try {
            if (error.response) errorData = await error.response.json();
        } catch (e) { /* ignore */ }
        if (error.response && error.response.status === 401) {
            showNotification("Session expired. Please log in again.", "error");
            handleLogout();
        } else {
            showNotification(errorData?.message || error.message || "An unknown error occurred.", "error");
        }
    }, [handleLogout, showNotification]);

    const renderView = () => {
        if (!user) return null;
        const viewProps = { user, handleApiError, showNotification, setView, fetchCompanySettings };
        if (user.role === 'SuperAdmin') return <SuperAdminView {...viewProps} />;

        const addons = companyDetails?.addons || {};

        const AccessDenied = () => (
            <div className="access-denied-container">
                <h2>Access Denied</h2>
                <p>You do not have permission to view this page.</p>
            </div>
        );

        const AddonRequired = ({ name }) => (
            <div className="access-denied-container">
                <h2>🔒 Add-On Required</h2>
                <p>The <strong>{name}</strong> module requires a paid add-on. Contact your administrator to enable it.</p>
            </div>
        );

        switch (view) {
            case 'new_sale': return <PointOfSaleView {...viewProps} />;
            case 'add_purchase': return <PurchaseBillView {...viewProps} />;
            case 'sales': return <SalesView {...viewProps} />;
            case 'purchase_history': return <PurchaseHistoryView {...viewProps} />;
            case 'profit_report': return (user.role === 'Admin' && (addons.profit_report || addons.profitReport)) ? <ProfitReportView {...viewProps} /> : (user.role !== 'Admin' ? <AccessDenied /> : <AddonRequired name="Profit Report" />);
            case 'closing_margin': return (user.role === 'Admin' && (addons.closing_margin || addons.closingMargin)) ? <ClosingMarginView {...viewProps} /> : (user.role !== 'Admin' ? <AccessDenied /> : <AddonRequired name="Closing Margin" />);
            case 'products': return (user.role === 'Admin' || user.role === 'Co-admin') ? <InventoryView {...viewProps} /> : <AccessDenied />;
            case 'expenses': return (user.role === 'Admin' || user.role === 'Co-admin') ? <ExpensesView {...viewProps} /> : <AccessDenied />;
            case 'settings': return <BillSettingsView {...viewProps} />;
            case 'leads': return <LeadsView {...viewProps} />;
            case 'customers': return (user.role === 'Admin' || user.role === 'Co-admin' || user.role === 'Member') ? <CustomersView {...viewProps} /> : <AccessDenied />;
            case 'tasks': return <TasksView {...viewProps} />;
            case 'users': return (user.role === 'Admin' || user.role === 'Co-admin') ? <UsersView {...viewProps} /> : <AccessDenied />;
            case 'billing': return (user.role === 'Admin') ? <BillingView {...viewProps} /> : <AccessDenied />;
            case 'payment_ledger': return (user.role === 'Admin' || user.role === 'Co-admin') ? <PaymentLedgerView {...viewProps} /> : <AccessDenied />;
            case 'dashboard':
            default:
                return (user.role === 'Admin' || user.role === 'Co-admin') ? <DashboardView {...viewProps} /> : <PointOfSaleView {...viewProps} />;
        }
    };

    let bannerMessage = null;
    let bannerType = 'warning'; // 'warning' or 'danger'

    if (user && user.role !== 'SuperAdmin' && companyDetails?.planEndDate) {
        const endDate = new Date(companyDetails.planEndDate);
        const now = new Date();
        const diffTime = endDate.getTime() - now.getTime();
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

        if (diffDays <= 0 && diffDays >= -15) {
            bannerType = 'danger';
            bannerMessage = `Your plan expired on ${endDate.toLocaleDateString()}. You are in a ${15 + diffDays}-day grace period before account suspension. Please contact support to renew immediately.`;
        } else if (diffDays > 0 && diffDays <= 15) {
            bannerMessage = `Your plan expires in ${diffDays} days on ${endDate.toLocaleDateString()}. Please contact support to renew your plan soon.`;
        }
    }

    return (
        <ThemeProvider>
            <Notification {...notification} />
            {bannerMessage && (
                <div style={{
                    backgroundColor: bannerType === 'danger' ? '#f44336' : '#ff9800',
                    color: 'white', position: 'fixed', top: 0, left: 0, right: 0,
                    zIndex: 9999, padding: '12px', textAlign: 'center', fontWeight: 'bold'
                }}>
                    {bannerMessage}
                </div>
            )}
            {user ? (
                <div className={`app-layout ${isSidebarOpen ? 'sidebar-open' : 'sidebar-closed'}`}>
                    {isSidebarOpen && window.innerWidth <= 768 && (
                        <div className="mobile-overlay" onClick={() => setIsSidebarOpen(false)}></div>
                    )}
                    <Sidebar view={view} setView={setView} handleLogout={handleLogout} user={user} companySettings={{ ...companySettings, addons: companyDetails?.addons || {} }} />
                    <div className="main-content-wrapper">
                        <header className="main-header">
                            <div className="header-left">
                                <button className="sidebar-toggle" onClick={() => setIsSidebarOpen(!isSidebarOpen)}>
                                    <svg viewBox="0 0 24 24"><path d="M3 18h18v-2H3v2zm0-5h18v-2H3v2zm0-7v2h18V6H3z"></path></svg>
                                </button>
                                <h1>{view.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}</h1>
                                {user?.role !== 'SuperAdmin' && companySettings?.subscriptionPlan && (
                                    <span className="plan-badge">
                                        ★ {companySettings.subscriptionPlan} Plan
                                    </span>
                                )}
                            </div>
                            <div className="header-right">
                                <span className="header-brand-name">Stockify</span>
                            </div>
                        </header>
                        <main className="main-content">{renderView()}</main>
                    </div>
                </div>
            ) : (
                <AuthPage onLoginSuccess={handleLoginSuccess} showNotification={showNotification} />
            )}
        </ThemeProvider>
    );
}

export default App;
